/* eslint-disable complexity */
import React, { useEffect, useState } from 'react';

import { Badge } from '@schoolinks/ui-components';
import cn from 'classnames';
import { isEmpty } from 'lodash';
import {
  shape,
  string,
  bool,
  node,
  oneOf,
  element,
  oneOfType,
  number,
} from 'prop-types';

import { faArrowDownToLine } from '@fortawesome/pro-regular-svg-icons/faArrowDownToLine';
import { faLock } from '@fortawesome/pro-regular-svg-icons/faLock';
import { faPencil } from '@fortawesome/pro-regular-svg-icons/faPencil';
import { faPlus } from '@fortawesome/pro-regular-svg-icons/faPlus';
import { faTrashCan } from '@fortawesome/pro-regular-svg-icons/faTrashCan';
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons/faArrowRight';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons/faCaretDown';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons/faChevronRight';

import ClickAwayListener from '@mui/material/ClickAwayListener';

import FontAwesomeIcon from 'components/Common/FontAwesomeIcon';

import Text from 'components/Common/Text';

import v from 'components/variables';

import s from './BaseButton.css';

const BaseButton = ({
  size,
  icon,
  label,
  style,
  variant,
  disabled,
  className,
  hasIconLeft,
  hasIconRight,
  tagName: Tag,
  selectedColor,
  compactVariant,
  hasWhiteBackground,
  hasNoWrap,
  innerLabel,
  textVariant,
  hasFocusUnderline,
  badgeCount,
  color,
  ...restProps
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [focusUnderline, setFocusUnderline] = useState(undefined);

  const isCompactSelect = compactVariant === 'select';
  const isCompactDefault = compactVariant === 'default';

  const isSmall = size === 'small';
  const isFilled = variant === 'filled';
  const isOutlined = variant === 'outlined';
  const isText = !isFilled && !isOutlined && !compactVariant;

  const isBlue = selectedColor === v.blueDark;
  const isRed = selectedColor === v.redDark;
  const isGray = selectedColor === v.grayDark;

  const showBadge =
    !isSmall &&
    (isOutlined || isCompactDefault) &&
    !hasIconRight &&
    badgeCount > 0;

  const transparent = hasWhiteBackground ? v.white : 'transparent';

  const bgColor =
    (isFilled && selectedColor) ||
    ((isOutlined || isText) && transparent) ||
    transparent;

  const textColor =
    (isFilled && v.white) ||
    ((isOutlined || isText) && selectedColor) ||
    v.blueDark;

  const commonIconMapping = {
    arrow: (
      <FontAwesomeIcon rotation={hasIconLeft ? 180 : 0} icon={faArrowRight} />
    ),
    chevron: (
      <FontAwesomeIcon
        icon={hasIconRight || hasIconLeft ? faChevronRight : faChevronDown}
        rotation={hasIconLeft ? 180 : 0}
      />
    ),
    edit: <FontAwesomeIcon icon={faPencil} />,
    plus: <FontAwesomeIcon icon={faPlus} />,
    trash: <FontAwesomeIcon icon={faTrashCan} />,
    lock: <FontAwesomeIcon icon={faLock} />,
    download: <FontAwesomeIcon icon={faArrowDownToLine} />,
  };
  const selectedIcon =
    typeof icon === 'string' ? commonIconMapping[icon] : icon;

  // source: https://stackoverflow.com/questions/21646738/convert-hex-to-rgba
  const hexToRGB = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);

    if (alpha) {
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
    return `rgb(${r}, ${g}, ${b})`;
  };

  const getBackgroundColor = () => {
    if (isHovered && !disabled) {
      const isBlueHover = isBlue;
      const isRedHover = isRed;
      const isGrayHover = isGray;

      if (isFilled) {
        if (isBlueHover) return v.blueXtraDark;
        if (isRedHover) return v.redXtraDark;
        if (isGrayHover) return v.grayBg;
      }

      if (isOutlined) {
        if (isBlueHover) return v.blueExtraLight;
        if (isRedHover) return v.redExtraLight;
        if (isGrayHover) return v.grayBg;
      }
    }
    return bgColor;
  };

  const getTextColor = () => {
    const isTransparent = bgColor === 'transparent';
    if (isHovered && isTransparent && !disabled) {
      const isBlueHover = isBlue;
      const isRedHover = isRed;
      const isGrayHover = isGray;

      if (!isFilled && !isOutlined) {
        if (isBlueHover) return v.blueXtraDark;
        if (isRedHover) return v.redXtraDark;
        if (isGrayHover) return v.grayXtraDark;
      }
    }
    return textColor;
  };

  const getSemanticColorName = hexColor => {
    let semanticColor = 'blue';
    if (hexColor) {
      semanticColor = Object.keys(v).find(key => v[key] === hexColor);
    }
    return semanticColor;
  };

  const getBorder = () =>
    selectedColor !== v.grayDark
      ? `solid 1px ${selectedColor}`
      : `solid 1px ${v.grayMedium}`;

  const setUnderlineOnFocus = () => {
    if (isText && hasFocusUnderline && !disabled) {
      setFocusUnderline(`2px solid ${hexToRGB(getTextColor(), 0.4)}`);
    }
  };

  // Removes focus underline if button is active and becomes disabled on click
  useEffect(() => {
    if (!disabled) {
      return;
    }

    setFocusUnderline(undefined);
  }, [disabled]);

  if (isCompactDefault || isCompactSelect) {
    return (
      <div className={s.compactButtonContainer}>
        <Tag
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          className={cn(className, s.compactButton, {
            [s.compactCircular]: !isSmall,
            [s.compactSmall]: isSmall,
            [s.compactSelect]: isCompactSelect,
            [s.compactSmallSelect]: isSmall && isCompactSelect,
            [s.disabled]: disabled,
            [s.blue]: isBlue,
            [s.red]: isRed,
            [s.gray]: isGray,

            [s.text]: isText,
          })}
          style={{
            border: getBorder(),
            backgroundColor: getBackgroundColor(),
            color: getTextColor(),
            whiteSpace: hasNoWrap ? 'nowrap' : undefined,
            ...style,
          }}
          {...restProps}
        >
          {/* TODO: expand the color and font control to non-compact variant as well later */}
          <Text
            variant={textVariant}
            color={getSemanticColorName(getTextColor())}
          >
            {innerLabel}
          </Text>
          {selectedIcon}
          {isCompactSelect && <FontAwesomeIcon icon={faCaretDown} />}
          {showBadge && (
            <Badge
              color={color}
              hasWhiteBorder
              number={badgeCount}
              classes={{ root: s.badge }}
            />
          )}
        </Tag>
        {label && (
          <div className={s.compactLabel} style={{ color: bgColor }}>
            {label}
          </div>
        )}
      </div>
    );
  }

  return (
    <ClickAwayListener onClickAway={() => setFocusUnderline(undefined)}>
      <Tag
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onFocus={setUnderlineOnFocus}
        className={cn(className, s.button, {
          [s.small]: isSmall,
          [s.withLabel]: !isEmpty(label),
          [s.withIconLeft]: hasIconLeft && !isSmall,
          [s.withIconRight]: hasIconRight && !isSmall,
          [s.smallWithIconLeft]: hasIconLeft && isSmall,
          [s.smallWithIconRight]: hasIconRight && isSmall,
          [s.disabled]: disabled,
          [s.blue]: isBlue,
          [s.red]: isRed,
          [s.gray]: isGray,

          [s.text]: isText,
        })}
        style={{
          ...(isFilled || isOutlined
            ? {
                border: getBorder(),
              }
            : { border: 'none', padding: 0 }),
          backgroundColor: getBackgroundColor(),
          color: getTextColor(),
          whiteSpace: hasNoWrap ? 'nowrap' : undefined,
          ...style,
        }}
        {...restProps}
      >
        {hasIconLeft && selectedIcon}
        {isText ? (
          <span
            style={{
              borderBottom: focusUnderline,
            }}
          >
            {label}
          </span>
        ) : (
          label
        )}
        {hasIconRight && selectedIcon}
        {showBadge ? (
          <Badge
            color={color}
            hasWhiteBorder
            number={badgeCount}
            classes={{ root: s.badge }}
          />
        ) : null}
      </Tag>
    </ClickAwayListener>
  );
};

BaseButton.propTypes = {
  icon: node,
  size: string,
  label: oneOfType([string, element]),
  disabled: bool,
  style: shape({}),
  className: string,
  hasIconLeft: bool,
  hasIconRight: bool,
  selectedColor: string,
  hasWhiteBackground: bool,
  hasNoWrap: bool,
  variant: oneOf(['filled', 'outlined']),
  tagName: oneOfType([string, element]),
  compactVariant: oneOf(['default', 'select']),
  // Specify an innerLabel value if you wish to display text within the button rather than below when compactVariant is 'select' or 'default'.
  innerLabel: string,
  hasFocusUnderline: bool,
  badgeCount: number,
  color: string,
  textVariant: string,
};

BaseButton.defaultProps = {
  label: '',
  style: {},
  size: null,
  icon: null,
  className: '',
  variant: null,
  disabled: false,
  tagName: 'button',
  hasIconLeft: false,
  selectedColor: null,
  hasIconRight: false,
  compactVariant: null,
  hasWhiteBackground: false,
  hasNoWrap: false,
  innerLabel: null,
  hasFocusUnderline: true,
  color: 'blue',
  textVariant: 'titleMd',
  badgeCount: undefined,
};

export default BaseButton;
